import { useState } from "react";
import { useStore } from "zustand";
import { useRobotState } from "../hooks/useRobotState";
import {
  isLiftCalibration,
  LIFT_CALIBRATION_KEY,
  rebaseLiftCalibration,
} from "../modules/robot-viewer/lift-calibration";
import type { LiftCalibration } from "../modules/robot-viewer/lift-calibration";
import styles from "./RobotModelPanel.module.scss";

export function LiftCalibrationPanel({
  calibration,
  onChange,
}: {
  calibration: LiftCalibration | null;
  onChange: (value: LiftCalibration | null) => void;
}) {
  const robotState = useRobotState();
  const position = useStore(robotState.armState, (state) => {
    const joints = state.joints;
    return joints?.position[joints.names.indexOf("body_joint")] ?? null;
  });
  const [editing, setEditing] = useState(false);
  const [bottom, setBottom] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);

  const currentPosition = () => {
    const state = robotState.armState.getState();
    const value = state.joints?.position[state.joints.names.indexOf("body_joint")];
    if (
      value === undefined ||
      !Number.isFinite(value) ||
      state.receivedAt === null ||
      Date.now() - state.receivedAt > 1000
    ) {
      setMessage("Нет свежих данных лифта. Дождитесь связи с роботом.");
      return null;
    }
    return value;
  };

  const save = (next: LiftCalibration) => {
    onChange(next);
    setEditing(false);
    setRestoring(false);
    try {
      localStorage.setItem(LIFT_CALIBRATION_KEY, JSON.stringify(next));
      setMessage(null);
    } catch {
      setMessage("Калибровка применена; браузер не разрешил её сохранить.");
    }
  };

  const capture = (endpoint: "bottom" | "top") => {
    const value = currentPosition();
    if (value === null) return;
    if (endpoint === "bottom") {
      setBottom(value);
      setMessage(null);
      return;
    }
    const next = { bottom, top: value };
    if (!isLiftCalibration(next)) {
      setMessage("Положения слишком близки. Поднимите лифт до верхней точки.");
      return;
    }
    save(next);
  };

  const restore = (endpoint: "bottom" | "top") => {
    const value = currentPosition();
    if (value === null || !calibration) return;
    save(rebaseLiftCalibration(calibration, endpoint, value));
  };

  return (
    <div className={styles.liftCalibration}>
      <div className={styles.liftStatus}>
        <span>Лифт: {position === null ? "нет данных" : `${position.toFixed(3)} м`}</span>
        <button
          type="button"
          onClick={() => {
            setEditing(!editing);
            setRestoring(false);
            setBottom(null);
            setMessage(null);
          }}
        >
          {editing ? "Отмена" : calibration ? "Перекалибровать" : "Калибровать"}
        </button>
      </div>
      {calibration && !editing && <span>Диапазон модели настроен</span>}
      {calibration && !editing && (
        <>
          <span>
            Низ: {calibration.bottom.toFixed(3)} м · Верх: {calibration.top.toFixed(3)} м
          </span>
          <button
            type="button"
            onClick={() => {
              setRestoring(!restoring);
              setMessage(null);
            }}
          >
            {restoring ? "Отмена восстановления" : "Восстановить после перезапуска"}
          </button>
          {restoring && (
            <>
              <p>
                Если после перезапуска изменилась высота модели, поставьте настоящий лифт в одну из
                крайних точек и отпустите управление. Выберите соответствующую точку. Прежний
                диапазон хода сохранится.
              </p>
              <button type="button" disabled={position === null} onClick={() => restore("bottom")}>
                Лифт сейчас внизу — совместить
              </button>
              <button type="button" disabled={position === null} onClick={() => restore("top")}>
                Лифт сейчас вверху — совместить
              </button>
              <p>Кнопки меняют только отображение модели.</p>
            </>
          )}
        </>
      )}
      {editing && (
        <>
          <p>
            {bottom === null
              ? "Опустите лифт до нижней точки и отпустите стик. Затем запомните положение."
              : "Поднимите лифт до верхней точки и отпустите стик. Затем запомните положение."}
          </p>
          <button
            type="button"
            disabled={position === null}
            onClick={() => capture(bottom === null ? "bottom" : "top")}
          >
            {bottom === null ? "Запомнить низ" : "Запомнить верх"}
          </button>
          {bottom !== null && <span>Низ: {bottom.toFixed(3)} м</span>}
          <p>Кнопки настраивают отображение. Двигайте лифт привычным управлением.</p>
        </>
      )}
      {message && <p role="status">{message}</p>}
    </div>
  );
}
